import { Injectable, inject } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { AgreementsService } from '@wilson/api/gateway';
import { FormService } from '@wilson/forms';
import {
  Agreement,
  AgreementProducts,
  AgreementStatus,
  ProductDTO,
  ProductUnit,
} from '@wilson/interfaces';
import { Currency } from '@wilson/localization';

export type ProductFormControls = {
  id: FormControl<string | null>;
  name: FormControl<string | null>;
  shortName: FormControl<string | null>;
  volume: FormControl<number | null>;
  unit: FormControl<ProductUnit | null>;
  rules: FormControl<string | null>;
  organizationalUnitId: FormControl<string | null>;
};

export type AgreementProductsFormControls = {
  agreementId: FormControl<string | null>;
  productId: FormControl<string | null>;
  quantity: FormControl<number | null>;
  product: FormGroup<ProductFormControls>;
};

export type AgreementBasicInfoFormControls = {
  name: FormControl<string | null>;
  status: FormControl<AgreementStatus | null>;
  volume: FormControl<number | null>;
  orderNumber: FormControl<string | null>;
  externalIdentifier: FormControl<string | null>;
  description: FormControl<string | null>;
};

export type AgreementIdentificationFormControls = {
  id: FormControl<string | null>;
  clientId: FormControl<string | null>;
};

export type AgreementFinancialFormControls = {
  vat: FormControl<string | number | null>;
  advancePayment: FormControl<string | number | null>;
  currency: FormControl<Currency | null>;
  companyCode: FormControl<string | null>;
};

export type AgreementAdditionalInfoFormControls = {
  purchaseOrder: FormControl<string | null>;
  costCenter: FormControl<string | null>;
};

export type AgreementFormControls = AgreementBasicInfoFormControls &
  AgreementIdentificationFormControls &
  AgreementFinancialFormControls &
  AgreementAdditionalInfoFormControls & {
    agreementProducts: FormArray<FormGroup<AgreementProductsFormControls>>;
  };

@Injectable()
export class AgreementsFormService extends FormService<Agreement> {
  protected readonly datasource = inject(AgreementsService);

  Currency = Currency;
  public readonly form = new FormGroup<AgreementFormControls>({
    name: new FormControl<Agreement['name']>('', [Validators.required]),
    status: new FormControl<Agreement['status']>(AgreementStatus.Open, [
      Validators.required,
    ]),
    volume: new FormControl<Agreement['volume']>(0),
    orderNumber: new FormControl<Agreement['orderNumber']>(''),
    externalIdentifier: new FormControl<Agreement['externalIdentifier']>(''),
    id: new FormControl<string | null>(null),
    purchaseOrder: new FormControl<Agreement['purchaseOrder']>(''),
    companyCode: new FormControl<Agreement['companyCode']>(''),
    currency: new FormControl<Currency | null>(null, [Validators.required]),
    vat: new FormControl<Agreement['vat']>(0, [Validators.required]),
    advancePayment: new FormControl<Agreement['advancePayment']>(0),
    description: new FormControl<Agreement['description']>(''),
    clientId: new FormControl<Agreement['clientId']>(''),
    costCenter: new FormControl<Agreement['costCenter']>(''),
    agreementProducts: new FormArray<FormGroup<AgreementProductsFormControls>>(
      [],
    ),
  });

  override initializeWithData(entity: Agreement): void {
    super.initializeWithData(entity);

    if (!entity.id) {
      // TODO: Replace with OrganizationalUnit values when VAT is added to the Locale
      this.form.patchValue({
        currency: Currency.EUR,
        vat: 19,
      });
      this.form.markAsPristine();
    }
  }

  get agreementProductsControl(): FormArray<
    FormGroup<AgreementProductsFormControls>
  > {
    return this.form.controls.agreementProducts;
  }

  private createAgreementProductsFormGroup(
    agreementProducts?: AgreementProducts,
  ): FormGroup<AgreementProductsFormControls> {
    return new FormGroup({
      agreementId: new FormControl(agreementProducts?.agreementId ?? null),
      productId: new FormControl(agreementProducts?.productId ?? null),
      quantity: new FormControl(agreementProducts?.quantity ?? null),
      product: new FormGroup<ProductFormControls>({
        id: new FormControl(agreementProducts?.product.id ?? null),
        name: new FormControl(agreementProducts?.product.name ?? null),
        shortName: new FormControl(
          agreementProducts?.product.shortName ?? null,
        ),
        volume: new FormControl(agreementProducts?.product.volume ?? null),
        unit: new FormControl(agreementProducts?.product.unit ?? null),
        rules: new FormControl(agreementProducts?.product.rules ?? null),
        organizationalUnitId: new FormControl(
          agreementProducts?.product.organizationalUnitId ?? null,
        ),
      }),
    });
  }

  setAgreementProducts(products: AgreementProducts[]): void {
    // Clear existing controls so we don't duplicate
    this.agreementProductsControl.clear();

    for (const product of products) {
      const agreementProductsGroup =
        this.createAgreementProductsFormGroup(product);
      this.agreementProductsControl.push(agreementProductsGroup);
    }
  }

  updateAgreementProducts(products: AgreementProducts[]): void {
    this.agreementProductsControl.clear();
    this.setAgreementProducts(products);
    this.form.controls.agreementProducts.markAsDirty();
  }

  updateProductInAgreementProducts(updated: ProductDTO): void {
    for (let i = 0; i < this.agreementProductsControl.length; i++) {
      const row = this.agreementProductsControl.at(i);
      const productGroup = row.controls.product;

      if (productGroup.controls.id.value !== updated.id) continue;
      productGroup.patchValue(
        {
          id: updated.id ?? null,
          name: updated.name ?? null,
          shortName: updated.shortName ?? null,
          volume: updated.volume ?? null,
          unit: updated.unit ?? null,
          rules: updated.rules ?? null,
          organizationalUnitId: updated.organizationalUnitId ?? null,
        },
        { emitEvent: true },
      );
      productGroup.markAsDirty();
    }
  }
}
